import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { Router } from '@angular/router';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { round } from 'mathjs';
import { Subject } from 'rxjs';
import { MultiColumnComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { NumericTextBoxComponent } from '@progress/kendo-angular-inputs';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';
import {
  Account,
  AccountsService,
  CurrenciesService,
  Currency,
  CurrencyRateDirectionType,
  CurrencyRatesService,
  JournalsService,
  Tax,
  TaxesService
} from '@api-accounting-client';
import { InfiniteScrollService } from '@app/shared/services';
import { ExpensesEditableGridModel } from '@app/accounting/expenses/expensesEditableGridModel';
import { InfiniteScrollDirective } from '@app/shared/directives/infinite-scroll.directive';
import { SideBarDataSharingService } from '@app/shared/services/side-bar';
import { GridEditingBaseComponent } from '@app/shared/bases';
import { DrawerDetails } from '@app/shared/models/drawer';
import { endOfTenantDay, isEmpty } from '@app/shared/helpers';

@Component({
    selector: 'app-expenses-editable-grid',
    templateUrl: './expenses-editable-grid.component.html',
    styleUrls: ['./expenses-editable-grid.component.scss'],
    providers: [InfiniteScrollService],
    standalone: false
})
export class ExpensesEditableGridComponent
  extends GridEditingBaseComponent<ExpensesEditableGridModel>
  implements OnInit, OnChanges, AfterViewInit, OnDestroy, AfterViewChecked {
  @ViewChildren(InfiniteScrollDirective)
  infiniteScrollDirective: QueryList<InfiniteScrollDirective>;
  @ViewChild('accountsDropdown')
  accountsDropdown: MultiColumnComboBoxComponent;
  @ViewChildren('rateTextboxes')
  rateTextboxes: QueryList<NumericTextBoxComponent>;
  @ViewChildren('currenciesDropdown')
  currenciesDropdown: QueryList<MultiColumnComboBoxComponent>;
  @ViewChildren('amountTextbox')
  amountTextboxes: QueryList<NumericTextBoxComponent>;
  @ViewChildren('rateLabels') rateLabels: QueryList<
    ElementRef<HTMLSpanElement>
  >;
  @ViewChildren('fetchButton') fetchButton: QueryList<
    ElementRef<HTMLButtonElement>
  >;
  reference: any;
  totalNet = 0;
  totalTax = 0;
  taxes: Tax[];
  taxesAll: Tax[] = [];
  totalAmount = 0;
  totalBaseAmount = 0;
  isNotBalanced = false;
  accounts: Account[] = [];
  hasAccountsLoaded = false;
  customValue: string;
  currencies: Currency[] = [];

  @Input() mode: string;
  @Input() parentForm: UntypedFormGroup;
  @Input() data: any[] = [];
  @Input() budgetItems: { uid: string; itemName: string }[] = [];
  @Output() checkDirty = new EventEmitter<boolean>();
  @Output() formChanged = new EventEmitter();
  baseCurrencyPrecision: number;
  trackChangeDetectionCounter = 0;

  @Input() totalCurrencyUid: string;

  filterChange = new Subject<string>();
  filterChange$ = this.filterChange.asObservable();

  public formatOptions: NumberFormatOptions = {
    style: 'currency',
    currency: '',
    currencyDisplay: 'code'
  };

  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected journalEntriesService: JournalsService,
    protected taxesService: TaxesService,
    protected router: Router,
    protected accountsService: AccountsService,
    protected currenciesService: CurrenciesService,
    private cd: ChangeDetectorRef,
    private currencyRatesService: CurrencyRatesService,
    public sideBarDataSharingService: SideBarDataSharingService,
    private infiniteScrollService: InfiniteScrollService
  ) {
    super(formBuilder);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.budgetItems) {
      console.log('[BUDGET_ITEMS] grid ngOnChanges — budgetItems:', this.budgetItems);
      // Force change detection so kendo-grid cell templates re-evaluate [data] binding
      this.cd?.detectChanges();
    }
  }

  ngAfterViewChecked(): void {
    while (this.trackChangeDetectionCounter < this.gridData.length) {
      this.trackChangeDetectionCounter++;
      if (this.trackChangeDetectionCounter === this.gridData.length) {
        if (this.mode === 'edit') {
          this.checkCurrencyValues();
          ((
            this.formArray().controls
          ) as UntypedFormArray[]).forEach((form, index) => {
            this.updateMoneyFormat(index, form.value.currency);
            this.updateMoneyValues(form.value);
          });
          this.populateRateLabels();
        }
        this.cd.detectChanges();
        return;
      }
    }
    this.checkBaseCurrency();
    this.cd.detectChanges();
  }

  async onLoadAccountsData(searchTerm: string = null): Promise<void> {
    const { totalRecordsSkip, pageSize } = this.infiniteScrollService;
    const result = await this.accountsService
      .accountsGet(
        this.utilsService.generateDataSourceRequestString(
          ['code', 'name'],
          searchTerm,
          {
            filters: [{
              field: 'accountSubType.rootType',
              operator: 'eq',
              value: 5
            }],
            logic: 'or'
          },
          [{ field: 'code', dir: 'asc' }],
          totalRecordsSkip,
          pageSize
        )
      )
      .toPromise();
    this.infiniteScrollService.setResponse(result);
    this.accounts = [...this.accounts, ...result?.items];
    // this.accounts = result?.items.map((el) => {
    //   return {
    //     uid: el.uid,
    //     name: el.name,
    //     //hierarchyLevel: el.accountType?.hierarchyLevel,
    //     // naturalBalanceType: el.naturalBalanceType,
    //     fullName: (el.uid + ' - ' + el.name).trim(),
    //   };
    // }).map((el: MappedAccount) => {
    //   if (el.hierarchyLevel) {
    //     const whiteSpace = ' ';
    //     el.uid = whiteSpace.repeat(el.hierarchyLevel) + el.uid;
    //   }
    //   return el;
    // });
  }

  onAccountsDropdownFocus(el: any) {
    // To load accounts initially
    if (!this.hasAccountsLoaded) {
      this.accounts = [];
      this.onLoadAccountsData();
      this.hasAccountsLoaded = true;
    }
    el.toggle(true);
  }

  async onAccountsFilterChange(value: string): Promise<void> {
    this.filterChange.next(value);
  }

  calculateTotals(): void {
    this.totalAmount = 0;
    (this.formArray().controls as UntypedFormGroup[])
      .slice(0, -1)
      .forEach((form) => {
        this.totalAmount += form.value.amount;
        this.totalNet += form.value.net;
      });
  }

  calculateNetPrices(): void {
    this.totalTax = 0;
    for (const form of this.formArray().controls.slice(
      0,
      -1
    ) as UntypedFormGroup[]) {
      const { amount, tax, net } = form.controls;
      if (amount.value === null) {
        net.setValue(null, { emitEvent: false });
        return;
      }
      if (amount.value !== null && tax.value !== null) {
        this.calculateTotalTax();
      }
      net.setValue(amount.value, { emitEvent: false });
    }
    this.formChanged.emit();
  }

  calculateTotalTax(): void {
    this.totalTax = 0;
    for (const form of this.formArray().controls.slice(0, -1) as UntypedFormGroup[]) {
      const { tax, amount } = form.controls;
      const taxRateControl = form.controls.taxRate;
      let totalTaxes = 0;
      if (tax.value) {
        //disabled for now (this one is for multiple tax per row)
        //const taxes: string[] = form.controls.tax.value;
        //for (const tax of taxes) {
        // const currTax = this.taxes.find((el) => el.uid === tax);

        let taxRate = this.taxes?.find(
          (el) => el.uid === tax.value
        ).rate;
        taxRate = taxRate > 0 ? taxRate : taxRateControl.value;
        if (taxRate) {
          totalTaxes += taxRate;
        }
        //}
        this.totalTax = this.totalTax + amount.value * totalTaxes;
      }
    }
  }

  triggerFormChange(): void {
    this.formArray().valueChanges.subscribe(() => {
      this.checkEditableGridState();
      this.setControlValidators();
      this.triggerValidators();
      this.calculateNetPrices();
      this.calculateBaseAmount();
      this.zeroValidation();
      this.calculateTotals();
      this.calculateTotalBaseAmount();
      this.checkTotalBaseAmount();
      this.checkCurrencyValues();
    });
  }

  getData(): UntypedFormArray {
    return this.formGroups.controls.gridForms as UntypedFormArray;
  }

  setControlValidators(): void {
    ((
      this.formArray().controls
    ) as UntypedFormGroup[]).forEach((form) => {
      const { accountUid, amount } = form.controls;
      accountUid.setValidators([Validators.required]);
      amount.setValidators([Validators.required]);
      form.updateValueAndValidity({ emitEvent: false });
    });
  }

  triggerValidators(): void {
    setTimeout(() => {
      this.formArray()?.markAllAsTouched();
      this.getFormGroup(this.formArray().length - 1).markAsUntouched();
    }, 0);
  }

  onTaxesValueChange(): void {
    this.triggerFormChange();
  }

  goToExpensesListPage(): void {
    this.router.navigate(['/expenses']);
  }

  checkTotalBaseAmount(): void {
    if (this.totalBaseAmount !== 0) {
      this.isNotBalanced = true;
    } else {
      this.isNotBalanced = false;
    }
    // this.formChanged.emit();
  }

  createAccount(accountName: string, index: string): void {
    const targetDetails: DrawerDetails = { rowIndex: index };
    this.sideBarDataSharingService.sendCreateAccountDrawerData(accountName, targetDetails);
    this.accountsDropdown.blur();
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    const sub1 = this.filterChange$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(async (value) => {
        //if user enter empty spacs
        if (value && isEmpty(value)) {
          return;
        }

        this.infiniteScrollService.resetPagination();
        this.accounts = [];
        //if user clears value
        if (!value) {
          this.customValue = '';
          await this.onLoadAccountsData();
          return;
        } //if user enters value with no empty spaces
        else if (value && !isEmpty(value)) {
          this.customValue = value.trim();
          await this.onLoadAccountsData(`"${this.customValue}"`);
        }
    });
    this.subscriptions.push(sub1);
    
    this.formatOptions.currency = this.appSettingsService.currenciesSettings.baseCurrency + ' ';
    const sub2 = this.sideBarDataSharingService.publishDataOnClose$.subscribe((data) => {
      if (data) {
        this.accounts.push(data);
        const target = this.sideBarDataSharingService.getTargetComponentDrawer();
        if (target && target.rowIndex != undefined) {
          const formGroup = this.getFormGroup(+target.rowIndex);
          if (formGroup.controls.accountUid) {
            formGroup.controls.accountUid.patchValue(data?.uid);
            this.sideBarDataSharingService.resetTargetComponentDrawer();
          }
        }
      }
    });
    this.subscriptions.push(sub2);

    // this.currencies = await this.currenciesService
    //   .currenciesGet()
    //   .pipe(map((x) => x.items))
    //   .toPromise();
    this.setControlValidators();
    this.triggerFormChange();
    // this.baseCurrencyPrecision = await this.currenciesService
    //   .currenciesUidGet('USD')
    //   .pipe(map((x) => x.precision))
    //   .toPromise();
    this.taxesService
      .taxesGet()
      .pipe(tap((x) => {
        this.taxesAll = x.items;
        this.taxes = this.taxesAll.filter((x) => x.isActive == true);
      }))
      .toPromise();
    this.checkCurrencyValues();
    this.checkBaseCurrency();
    if (this.mode === 'preview') {
      this.formArray().clear();
      this.gridData = [];
    }
    if (this.mode === 'edit') {
      this.gridData = [...(this.data?.length > 0 ? this.data : [])];
      this.addNewRow(this.formArray(), this.gridData);
      this.formArray().clear();
      this.gridData.forEach((record) => {
        const group: any = {};
        Object.entries(record).forEach(([key, value]) => {
          group[key] = new UntypedFormControl(value);
        });
        const formGroup = new UntypedFormGroup(group);
        this.formArray().push(formGroup);
        this.prepopulateAccounts(record);
      });
      this.setControlValidators();
      this.calculateNetPrices();
      this.calculateTotals();
      const currentSubscription = ((
        this.getFormGroup(this.gridData.length - 1)
      ) as UntypedFormGroup).valueChanges.subscribe(() => {
        this.createNewRowHandler(currentSubscription);
      });
      this.triggerFormChange();
    }
  }

  ngAfterViewInit(): void {
    // Handle scroll event on page load
    this.handleAccountsDropdownsScroll(this.infiniteScrollDirective);
    // Handle scroll event when a row in grid, gets added/removed at runtime
    this.infiniteScrollDirective.changes
    .subscribe(items => {
      this.handleAccountsDropdownsScroll(items);
    });
    // Get Accounts for Infinite scroll purpose
    // When scrollbar hit the bottom
    this.infiniteScrollService.getData$.subscribe(() => {
      this.onLoadAccountsData();
    });
  }

  // Fire the scrollbar event of Accounts dropdowns
  // when it hits the bottom
  private handleAccountsDropdownsScroll(items: QueryList<InfiniteScrollDirective>) {
    items.forEach(element => {
      // Reset EventEmitter, in case this function gets called multiple times
      element.scrolled = new EventEmitter();
      element.scrolled.subscribe(() => {
        this.infiniteScrollService.onScrolled();
      });
    });
  }

  private prepopulateAccounts(record: ExpensesEditableGridModel) {
    this.accounts.push({
      uid: record.accountUid,
      name: record.accountName,
      fullName: record.accountName
    });
    if (record.tax) {
      this.taxesAll.push({
        uid: record.tax,
        name: record.taxName,
        rate: record.taxRate
      });
    }
  }

  private formArray(): UntypedFormArray {
    return this.formGroups?.controls.gridForms as UntypedFormArray;
  }

  private getFormGroup(index: number): UntypedFormGroup {
    return this.formArray().at(index) as UntypedFormGroup;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.infiniteScrollService.getData.unsubscribe();
  }

  checkEditableGridState(): void {
    if (this.formArray().dirty) {
      this.checkDirty.emit(true);
    }
  }

  checkCurrencyValues(): void {
    if (this.rateTextboxes?.length > 0) {
      setTimeout(() => {
        this.formArray().controls.forEach(
          (form: UntypedFormGroup, index) => {
            const { currency } = form.controls;
            if (currency.value === 'USD') {
              this.rateTextboxes.toArray()[index].disabled = true;
            } else if (
              currency.value !== 'USD' &&
              currency.value
            ) {
              this.rateTextboxes.toArray()[index].disabled = false;
            } else if (!currency.value) {
              this.rateTextboxes.toArray()[index].disabled = true;
            }
          }
        );
      }, 0);
    }
  }

  onLoadData(): any {
    return {};
  }

  onModelCreated(): ExpensesEditableGridModel {
    return new ExpensesEditableGridModel();
  }

  async onAccountsValueChange(value: string, dataItem: any): Promise<void> {
    this.populateCurrencyField(value, dataItem.rowId);
    this.updateMoneyFormat(dataItem.rowId, dataItem.currency);
    this.updateMoneyValues(dataItem);
    const formGroup = this.getFormGroup(dataItem.rowId);
    const { remark, amount, tax, net } = formGroup.controls;
    remark?.setValue(null, { emitEvent: false });
    amount?.setValue(0, { emitEvent: false });
    tax?.setValue(null, { emitEvent: false });
    net?.setValue(0, { emitEvent: false });
    this.formChanged.emit();
  }

  zeroValidation(): void {
    (this.formArray().controls as UntypedFormGroup[])
      .slice(0, -1)
      .forEach((form) => {
        const amount = form.controls.amount;
        if (!amount?.value) {
          amount.clearValidators();
          amount.setValidators([Validators.min(1), Validators.required]);
          amount.markAsTouched({ onlySelf: true });
          form.updateValueAndValidity({ emitEvent: false });
        } else {
          amount.clearValidators();
          amount.setValidators([Validators.min(0), Validators.required]);
          form.updateValueAndValidity({ emitEvent: false });
        }
        this.updateMoneyValues(form.value);
      });
  }

  checkBaseCurrency(): void {
    if (this.fetchButton?.length > 0) {
      ((
        this.formArray().controls
      ) as UntypedFormGroup[]).forEach((form, index) => {
        const { currency } = form.controls;
        if (!currency.value) {
          this.fetchButton.toArray()[index].nativeElement.disabled = true;
        } else if (currency.value === 'USD') {
          this.fetchButton.toArray()[index].nativeElement.disabled = true;
        } else if (
          currency.value !== 'USD' &&
          currency.value
        ) {
          this.fetchButton.toArray()[index].nativeElement.disabled = false;
        }
      });
    }
  }

  calculateTotalBaseAmount(): void {
    this.totalBaseAmount = 0;
    ((
      this.formArray().controls
    ) as UntypedFormGroup[]).forEach((form) => {
      this.totalBaseAmount += form.controls.baseAmount.value;
    });
    // this.formChanged.emit();
  }

  async populateCurrencyField(value: string, rowIndex: number): Promise<void> {
    if (!value) {
      return;
    }
    const formGroup = this.getFormGroup(rowIndex);
    // const { currency, accountUid, rate, amount, baseAmount } = formGroup.controls;

    // currency.patchValue(
    //   this.accounts.find(
    //     (account) => account?.uid === accountUid.value
    //   )?.currency?.uid
    // );    

    // if (
    //   currency.value === 'USD' &&
    //   currency.value
    // ) {
    //   rate.patchValue(1, { emitEvent: false });
    //   if (this.rateTextboxes.length > 0) {
    //     this.rateTextboxes.toArray()[rowIndex].disabled = true;
    //     this.rateLabels.toArray()[rowIndex].nativeElement.textContent = '';
    //   }
    // } else if (currency.value !== 'USD') {
    //   if (this.rateTextboxes.length > 0) {
    //     this.rateTextboxes.toArray()[rowIndex].disabled = false;
    //     this.rateLabels.toArray()[
    //       rowIndex
    //     ].nativeElement.textContent = `1 USD for ${currency.value}`;
    //   }
    // } else {
    //   if (this.rateTextboxes.length > 0) {
    //     this.rateTextboxes.toArray()[rowIndex].disabled = true;
    //     this.rateLabels.toArray()[rowIndex].nativeElement.textContent = '';
    //   }
    // }
    // if (amount.value) {
    //   baseAmount.setValue(
    //     round(
    //       (amount.value) * rate.value,
    //       2
    //     )
    //   );
    // }
    formGroup.patchValue(formGroup.value, { emitEvent: false });
  }

  populateRateLabels(): void {
    if (this.rateLabels.length > 0) {
      (this.formArray().controls as UntypedFormGroup[])
        .slice(0, -1)
        .forEach((form, index) => {
          const { currency } = form.controls;
          if (
            currency.value !== 'USD' &&
            currency.value
          ) {
            this.rateLabels.toArray()[
              index
            ].nativeElement.textContent = `1 USD for ${currency.value}`;
          } else if (currency.value === 'USD') {
            this.rateLabels.toArray()[index].nativeElement.textContent = '';
          } else if (!currency.value) {
            this.rateLabels.toArray()[index].nativeElement.textContent = '';
          }
        });
    }
  }

  updateMoneyValues(dataItem: any): void {
    const formGroup = this.getFormGroup(dataItem.rowId);
    const { amount } = formGroup.controls;

    if (amount.value > 0) {
      amount.setValue(amount.value, { emitEvent: false });
    } else {
      amount.setValue(dataItem.amount, { emitEvent: false });
    }
    this.formChanged.emit();
  }

  updateMoneyFormat(rowId: number, value: string): void {
    // let formatOptions: NumberFormatOptions | string;
    if (value == null) {
      // formatOptions = {
      //   currency: '',
      //   currencyDisplay: 'code',
      //   style: 'currency'
      // };
      // this.formatOptions.currency = this.appSettingsService.currenciesSettings.baseCurrency + ' ';
    } else {
      // formatOptions = {
      //   currency: `${value} `,
      //   currencyDisplay: 'code',
      //   style: 'currency'
      // };
    }
    // this.amountTextboxes.toArray()[rowId].format = formatOptions;
  }

  async onCurrencyValueChange(value: string, dataItem: any): Promise<void> {
    this.updateMoneyFormat(dataItem.rowId, value);
    this.updateMoneyValues(dataItem);
    const formGroup = this.getFormGroup(dataItem.rowId);
    this.currenciesDropdown.toArray().forEach(async (x, index) => {
      if (x.value !== 'USD' && x.value) {
        this.rateTextboxes.toArray()[index].disabled = false;
        this.fetchButton.toArray()[index].nativeElement.disabled = false;
      } else if (x.value === 'USD') {
        this.rateTextboxes.toArray()[index].disabled = true;
        this.fetchButton.toArray()[index].nativeElement.disabled = true;
        formGroup.controls.rate.setValue(1, { emitEvent: false });
      } else if (!x.value) {
        this.rateTextboxes.toArray()[index].writeValue(null);
        this.rateTextboxes.toArray()[index].disabled = true;
        this.fetchButton.toArray()[index].nativeElement.disabled = true;
        this.rateLabels.toArray()[index].nativeElement.textContent = '';
      }
    });
    await this.populateRateBasedOnCurrency(formGroup, value, dataItem.rowId);
    this.formChanged.emit();
  }

  async populateRateBasedOnCurrency(
    formGroup: UntypedFormGroup,
    value: string,
    index: number
  ): Promise<void> {
    if (!value) {
      return;
    }
    const { rate } = formGroup.controls;
    const currencyControl = formGroup.controls.currency;
    let currencyRateHint = '';
    const currency = await this.currenciesService
      .currenciesUidGet(formGroup.controls.currency.value)
      .toPromise();
    const currencyRate = await this.currencyRatesService
      .currencyRatesSymbolRatesLatestGet(
        currencyControl.value,
        endOfTenantDay(this.parentForm?.controls?.date?.value)
      )
      .toPromise();
    if (
      currency.defaultRateDirectionType === CurrencyRateDirectionType.FromBase
    ) {
      currencyRateHint = `USD for 1 ${currencyControl.value.toUpperCase()}`;
      if (currencyRate !== null && currencyRate !== undefined) {
        rate.patchValue(currencyRate.rateFromBase);
      } else {
        rate.patchValue(1);
      }
    } else {
      currencyRateHint = `${currencyControl.value.toUpperCase()} for 1 USD`;
      if (currencyRate !== null && currencyRate !== undefined) {
        rate.patchValue(currencyRate.rateToBase);
      } else {
        rate.patchValue(1);
      }
    }
    if (value !== 'USD') {
      this.rateLabels.toArray()[index].nativeElement.textContent =
        currencyRateHint;
    } else {
      this.rateLabels.toArray()[index].nativeElement.textContent = '';
    }
    this.calculateBaseAmount();
    this.calculateTotalBaseAmount();
  }

  async fetchRate(dataItem: any): Promise<void> {
    let currencyRateLabel = '';
    const formGroup = this.getFormGroup(dataItem.rowId);
    const currencyValue = formGroup.controls.currency.value;
    const { rate } = formGroup.controls;
    const currencyRate = await this.currencyRatesService
      .currencyRatesSymbolRatesLatestGet(
        currencyValue,
        endOfTenantDay(this.parentForm?.controls?.date?.value)
      )
      .toPromise();
    const currency = await this.currenciesService
      .currenciesUidGet(currencyValue)
      .toPromise();
    if (
      currency.defaultRateDirectionType === CurrencyRateDirectionType.FromBase
    ) {
      currencyRateLabel = `USD for 1 ${currencyValue}`;
      if (currencyRate !== null && currencyRate !== undefined) {
        rate.patchValue(
          round(currencyRate.rateFromBase, 8),
          {
            emitEvent: false
          }
        );
      } else {
        rate.patchValue(1, {
          emitEvent: false
        });
      }
    } else {
      currencyRateLabel = `${currencyValue} for 1 USD`;
      if (currencyRate !== null && currencyRate !== undefined) {
        rate.patchValue(
          round(currencyRate.rateToBase, 8),
          {
            emitEvent: false
          }
        );
      } else {
        rate.patchValue(1, {
          emitEvent: false
        });
      }
    }
    if (currencyValue !== 'USD') {
      this.rateLabels.toArray()[dataItem.rowId].nativeElement.textContent =
        currencyRateLabel;
    } else {
      this.rateLabels.toArray()[dataItem.rowId].nativeElement.textContent = '';
    }
    this.calculateBaseAmount();
    this.calculateTotalBaseAmount();
    // this.balanceNewRow();
  }

  calculateBaseAmount(): void {
    // ((
    //   this.formArray().controls
    // ) as UntypedFormGroup[]).forEach(async (form) => {
    //   const { amount, rate } = form.controls;
    //  const baseAmountControl = form.controls.baseAmount;
    //   if (
    //     (amount.value >= 0) &&
    //     rate.value
    //   ) {
    //     const currency = this.currencies.find(x => x?.uid === currency.value);
        
    //     if (currency?.defaultRateDirectionType === CurrencyRateDirectionType.ToBase) {
    //       const baseAmount = round(
    //         (amount.value) /
    //         rate.value,
    //         this.baseCurrencyPrecision
    //       );
    //       baseAmountControl.setValue(baseAmount, { emitEvent: false });

    //     } else if (currency?.defaultRateDirectionType === CurrencyRateDirectionType.FromBase) {
    //       const baseAmount = round(
    //         (amount.value) *
    //         rate.value,
    //         this.baseCurrencyPrecision
    //       );
    //       baseAmountControl.setValue(baseAmount, { emitEvent: false });
    //     }

    //   } else {
    //     amount.setValidators([Validators.required]);
    //   }
    // });
    // this.formChanged.emit();
  }
}
